import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import Sidebar from './components/Sidebar.tsx';
import ChatWindowV2 from './components/ChatWindowV2.tsx';
import ModeSelector from './components/ModeSelector.tsx';
import SessionTabs from './components/SessionTabs.tsx';
import FileTree from './components/FileTree.tsx';
import CoworkPanel from './components/CoworkPanel.tsx';

export interface Conversation {
  id: string;
  mode: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  pinned: boolean;
  messageCount: number;
}

export interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  createdAt: string;
  tokensIn?: number;
  tokensOut?: number;
  durationMs?: number;
  attachedFile?: { name: string; type: string };
}

export type SessionTab = 'chat' | 'cowork' | 'coding';

interface ActiveFile {
  path: string;
  content: string;
  name: string;
}

const API_URL = 'https://veritan-api.veritan.workers.dev/api';

export default function App() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversation, setActiveConversation] = useState<string | null>(null);
  const [currentMode, setCurrentMode] = useState('GOVERNANCE');
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [sessionTab, setSessionTab] = useState<SessionTab>('chat');
  const [workspace, setWorkspace] = useState<string | null>(null);

  // File system
  const [dirHandle, setDirHandle] = useState<any | null>(null);
  const [activeFile, setActiveFile] = useState<ActiveFile | null>(null);
  const [fileEditing, setFileEditing] = useState(false);
  const [fileEditContent, setFileEditContent] = useState('');
  const [fileSaving, setFileSaving] = useState(false);

  // Live streaming stats
  const [streamingTokensIn, setStreamingTokensIn] = useState(0);
  const [streamingTokensOut, setStreamingTokensOut] = useState(0);
  const [streamingElapsed, setStreamingElapsed] = useState(0);
  const streamStartRef = useRef<number | null>(null);
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => { loadConversations(); }, []);

  useEffect(() => {
    if (activeConversation) loadMessages(activeConversation);
  }, [activeConversation]);

  useEffect(() => {
    if (loading) {
      streamStartRef.current = Date.now();
      elapsedTimerRef.current = setInterval(() => {
        setStreamingElapsed(Math.floor((Date.now() - (streamStartRef.current || Date.now())) / 1000));
      }, 1000);
    } else {
      if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
      setStreamingElapsed(0);
      setStreamingTokensIn(0);
      setStreamingTokensOut(0);
    }
    return () => { if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current); };
  }, [loading]);

  async function loadConversations() {
    try {
      const r = await fetch(`${API_URL}/conversations`);
      const d = await r.json();
      setConversations(d.conversations || []);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    }
  }

  async function loadMessages(conversationId: string) {
    try {
      const r = await fetch(`${API_URL}/conversations/${conversationId}`);
      const d = await r.json();
      setMessages(d.messages || []);
      setCurrentMode(d.conversation?.mode || currentMode);
    } catch (err) {
      console.error('Failed to load messages:', err);
    }
  }

  async function createNewConversation(mode: string) {
    try {
      setLoading(true);
      const r = await fetch(`${API_URL}/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode })
      });
      const d = await r.json();
      if (d.success) {
        const newConv: Conversation = {
          id: d.conversation.id,
          mode: d.conversation.mode,
          title: d.conversation.title,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          pinned: false,
          messageCount: 0,
        };
        setConversations(prev => [newConv, ...prev]);
        setActiveConversation(d.conversation.id);
        setCurrentMode(mode);
        setMessages([]);
      }
    } catch (err) {
      console.error('Failed to create conversation:', err);
    } finally {
      setLoading(false);
    }
  }

  // sendMessage accepts optional fileContext: injected into API call, shown as attachment badge in chat
  async function sendMessage(content: string, file?: File, fileContext?: { name: string; content: string }) {
    if (!activeConversation || !content.trim()) return;
    const msgStart = Date.now();
    try {
      setLoading(true);

      type FilePayload = { name: string; type: string; text?: string; base64?: string; binary_desc?: boolean; size_kb?: number };
      let filePayload: FilePayload | undefined;

      if (file) {
        filePayload = { name: file.name, type: file.type };
        const TEXT_EXTS = new Set(['txt','md','markdown','html','htm','css','js','jsx','ts','tsx','json','sql','csv','yaml','yml','xml','toml','sh','bash','py','go','rb','rs','java','c','cpp','h','hpp','swift','kt','php','r','m','svg','vue','svelte','graphql','gql','env','config','conf','ini','log']);
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        if (TEXT_EXTS.has(ext) || file.type.startsWith('text/')) {
          filePayload.text = await file.text();
        } else if (file.type.startsWith('image/')) {
          const buffer = await file.arrayBuffer();
          const bytes = new Uint8Array(buffer);
          let binary = '';
          bytes.forEach(b => { binary += String.fromCharCode(b); });
          filePayload.base64 = btoa(binary);
        } else {
          filePayload.binary_desc = true;
          filePayload.size_kb = Math.round(file.size / 1024);
        }
      }

      // User message shown in chat uses original content
      const userMessage: Message = {
        id: Date.now().toString(),
        role: 'user',
        content,
        attachedFile: fileContext
          ? { name: fileContext.name, type: 'text/context' }
          : (filePayload ? { name: filePayload.name, type: filePayload.type } : undefined),
        createdAt: new Date().toISOString(),
      };
      setMessages(prev => [...prev, userMessage]);

      // API receives context-augmented content
      const apiContent = fileContext
        ? `[File: ${fileContext.name}]\n\`\`\`\n${fileContext.content.slice(0, 8000)}${fileContext.content.length > 8000 ? '\n…[truncated]' : ''}\n\`\`\`\n\n${content}`
        : content;

      const response = await fetch(`${API_URL}/conversations/${activeConversation}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: apiContent, file: filePayload }),
      });

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let assistantContent = '';
      let assistantMessageId = '';
      let finalTokensIn = 0;
      let finalTokensOut = 0;

      while (reader) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event = JSON.parse(line.slice(6));
              if (event.type === 'stream') {
                assistantContent += event.content;
                setStreamingTokensOut(Math.floor(assistantContent.length / 4));
              } else if (event.type === 'done') {
                assistantMessageId = event.messageId;
                if (event.tokens_in) { finalTokensIn = event.tokens_in; setStreamingTokensIn(event.tokens_in); }
                if (event.tokens_out) { finalTokensOut = event.tokens_out; }
              }
            } catch {}
          }
        }
      }

      if (assistantContent) {
        const durationMs = Date.now() - msgStart;
        const assistantMessage: Message = {
          id: assistantMessageId || Date.now().toString(),
          role: 'assistant',
          content: assistantContent,
          createdAt: new Date().toISOString(),
          tokensIn: finalTokensIn || undefined,
          tokensOut: finalTokensOut || Math.floor(assistantContent.length / 4),
          durationMs,
        };
        setMessages(prev => [...prev, assistantMessage]);
      }

      await loadConversations();
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setLoading(false);
    }
  }

  // Code mode: wraps sendMessage with active file context injection
  function sendMessageCodeMode(content: string, file?: File) {
    if (activeFile) {
      const ctx = fileEditing ? fileEditContent : activeFile.content;
      sendMessage(content, file, { name: activeFile.name, content: ctx });
    } else {
      sendMessage(content, file);
    }
  }

  function handleFolderPick(handle: any, name: string) {
    setDirHandle(handle);
    setWorkspace(name);
    setActiveFile(null);
    setFileEditing(false);
  }

  function handleFileSelect(path: string, content: string, name: string) {
    setActiveFile({ path, content, name });
    setFileEditing(false);
    setFileEditContent(content);
  }

  async function saveFile() {
    if (!activeFile || !dirHandle || !fileEditing) return;
    setFileSaving(true);
    try {
      const parts = activeFile.path.split('/');
      let currentDir: any = dirHandle;
      for (let i = 0; i < parts.length - 1; i++) {
        currentDir = await currentDir.getDirectoryHandle(parts[i]);
      }
      const fileHandle = await currentDir.getFileHandle(parts[parts.length - 1]);
      const writable = await fileHandle.createWritable();
      await writable.write(fileEditContent);
      await writable.close();
      setActiveFile({ ...activeFile, content: fileEditContent });
      setFileEditing(false);
    } catch (err) {
      console.error('Save failed:', err);
      alert('Save failed — check browser permissions');
    } finally {
      setFileSaving(false);
    }
  }

  async function pinConversation(id: string, pinned: boolean) {
    setConversations(prev => prev.map(c => c.id === id ? { ...c, pinned } : c));
  }

  async function deleteConversation(id: string) {
    setConversations(prev => prev.filter(c => c.id !== id));
    if (activeConversation === id) {
      setActiveConversation(null);
      setMessages([]);
    }
  }

  // Code mode: determine language for display
  const codeLanguage = activeFile?.name.split('.').pop()?.toLowerCase() || 'text';

  return (
    <div className="app">
      <div className="app-layout">
        <Sidebar
          conversations={conversations}
          activeConversation={activeConversation}
          workspace={workspace}
          onSelectConversation={setActiveConversation}
          onNewChat={() => createNewConversation(currentMode)}
          onPinConversation={pinConversation}
          onDeleteConversation={deleteConversation}
          onFolderPick={handleFolderPick}
          loading={loading}
        />

        <div className="main-content">
          <div className="header">
            <ModeSelector currentMode={currentMode} onModeChange={mode => createNewConversation(mode)} />
            <SessionTabs activeTab={sessionTab} onTabChange={setSessionTab} />
          </div>

          {/* COWORK MODE */}
          {sessionTab === 'cowork' && <CoworkPanel />}

          {/* CODE MODE */}
          {sessionTab === 'coding' && (
            <div className="code-mode-layout">
              {/* File tree */}
              <div className="code-tree-pane">
                {dirHandle ? (
                  <FileTree
                    dirHandle={dirHandle}
                    onFileSelect={handleFileSelect}
                    selectedPath={activeFile?.path}
                    rootName={workspace || 'Workspace'}
                  />
                ) : (
                  <div className="code-no-folder">
                    <span>📁</span>
                    <p>Open a folder<br />from the sidebar</p>
                  </div>
                )}
              </div>

              {/* Editor pane */}
              <div className="code-editor-pane">
                {activeFile ? (
                  <>
                    <div className="code-editor-header">
                      <div className="code-file-info">
                        <span className="code-file-dot" />
                        <span className="code-file-name">{activeFile.name}</span>
                        <span className="code-file-path">{activeFile.path}</span>
                      </div>
                      <div className="code-editor-actions">
                        <button
                          className="code-action-btn"
                          onClick={() => navigator.clipboard.writeText(fileEditing ? fileEditContent : activeFile.content)}
                          title="Copy to clipboard"
                        >
                          Copy
                        </button>
                        {fileEditing ? (
                          <>
                            <button
                              className="code-action-btn code-save-btn"
                              onClick={saveFile}
                              disabled={fileSaving}
                            >
                              {fileSaving ? 'Saving…' : 'Save'}
                            </button>
                            <button
                              className="code-action-btn"
                              onClick={() => { setFileEditing(false); setFileEditContent(activeFile.content); }}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            className="code-action-btn"
                            onClick={() => { setFileEditing(true); setFileEditContent(activeFile.content); }}
                          >
                            Edit
                          </button>
                        )}
                      </div>
                    </div>
                    {fileEditing ? (
                      <textarea
                        className="code-textarea"
                        value={fileEditContent}
                        onChange={e => setFileEditContent(e.target.value)}
                        spellCheck={false}
                        autoCorrect="off"
                        autoCapitalize="off"
                      />
                    ) : (
                      <pre className="code-display">
                        <code className={`lang-${codeLanguage}`}>{activeFile.content}</code>
                      </pre>
                    )}
                  </>
                ) : (
                  <div className="code-no-file">
                    <span>📄</span>
                    <p>Select a file<br />from the tree</p>
                  </div>
                )}
              </div>

              {/* Chat pane */}
              <div className="code-chat-pane">
                <ChatWindowV2
                  messages={messages}
                  mode="TECHNOLOGY"
                  onSendMessage={sendMessageCodeMode}
                  loading={loading}
                  streamingTokensIn={streamingTokensIn}
                  streamingTokensOut={streamingTokensOut}
                  streamingElapsed={streamingElapsed}
                  workspace={workspace}
                  isCodingMode={true}
                />
              </div>
            </div>
          )}

          {/* CHAT MODE */}
          {sessionTab === 'chat' && (
            <ChatWindowV2
              messages={messages}
              mode={currentMode}
              onSendMessage={sendMessage}
              loading={loading}
              streamingTokensIn={streamingTokensIn}
              streamingTokensOut={streamingTokensOut}
              streamingElapsed={streamingElapsed}
              workspace={workspace}
            />
          )}
        </div>
      </div>
    </div>
  );
}
